import React, { useEffect, useId } from 'react'
import clsx from 'clsx'
import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import './ApplyV2InputRate.scss'

interface Props {
    value?: number | null
    label?: string
    onChange?: (value: number) => void
    onFocus?: React.FocusEventHandler<HTMLButtonElement>
    onBlur?: React.FocusEventHandler<HTMLButtonElement>
    isDisabled?: boolean
    className?: string
    id?: string | number
    name?: string
    isLoading?: boolean
    errorMessage?: string | null | undefined | boolean
    isRequired?: boolean
    isShowErrorMessage?: boolean
    maxRating?: number
    description?: React.ReactNode
}

const baseClass = 'ApplyV2InputRate'

function ApplyV2InputRate({
    value = 0,
    label,
    onChange,
    onFocus,
    onBlur,
    isDisabled = false,
    className = '',
    id,
    name,
    isLoading = false,
    errorMessage,
    isRequired = false,
    isShowErrorMessage = true,
    maxRating = 5,
    description,
}: Props) {
    const defaultId = useId()
    const starsRef = React.useRef<HTMLDivElement>(null)
    const [focused, setFocused] = React.useState(0)

    const handleRateChange = (newValue: number) => {
        if (!isDisabled && !isLoading && onChange) {
            onChange(newValue)
        }
    }
    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--error`]: !!errorMessage,
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--loading`]: isLoading,
            })}
            data-error={!!errorMessage ? 'true' : 'false'}
        >
            {label && (
                <label className={`${baseClass}__label`} htmlFor={id ? id.toString() : defaultId}>
                    {label}
                    {isRequired && '*'}
                </label>
            )}
            <div
                className={clsx(`${baseClass}__input-wrapper`, {
                    [`${className}__input-wrapper`]: className,
                    [`${baseClass}__input-wrapper--disabled`]: isDisabled,
                    [`${baseClass}__input-wrapper--loading`]: isLoading,
                    [`${baseClass}__input-wrapper--error`]: !!errorMessage,
                    [`${className}__input-wrapper--disabled`]: className && isDisabled,
                    [`${className}__input-wrapper--loading`]: className && isLoading,
                    [`${className}__input-wrapper--error`]: className && !!errorMessage,
                })}
            >
                <div
                    ref={starsRef}
                    className={`${baseClass}__stars`}
                    onMouseLeave={() => {
                        setFocused(0)
                        if (
                            document.activeElement instanceof HTMLElement &&
                            starsRef.current?.contains(document.activeElement)
                        ) {
                            document.activeElement.blur()
                        }
                    }}
                >
                    {[...Array(maxRating)].map((_, index) => {
                        const starValue = index + 1
                        return (
                            <button
                                key={starValue}
                                name={name}
                                type="button"
                                // tabIndex={-1}
                                className={clsx(`${baseClass}__star`, {
                                    [`${baseClass}__star--active`]: starValue <= (value || 0),
                                    [`${baseClass}__star--hover`]: starValue <= (value || 0),
                                    [`${baseClass}__star--disabled`]: isDisabled,
                                    [`${baseClass}__star--error`]: !!errorMessage,
                                    [`${baseClass}__star--${starValue}`]: starValue,
                                    [`${baseClass}__star--focused`]: starValue <= focused,
                                })}
                                onClick={() => {
                                    handleRateChange(starValue)
                                    setFocused(0)
                                }}
                                onFocus={(e) => {
                                    if (onFocus) {
                                        onFocus(e)
                                    }
                                    setFocused(starValue)
                                }}
                                onBlur={(e) => {
                                    if (onBlur) {
                                        onBlur(e)
                                    }
                                    setFocused(0)
                                }}
                                onMouseOver={(e) => {
                                    setFocused(starValue)
                                }}
                                disabled={isDisabled || isLoading}
                                aria-label={`Rate ${starValue} out of ${maxRating}`}
                            >
                                {/*{starValue <= (value || 0) ? '★' : '☆'}*/}
                                {starValue <= focused || starValue <= (value || 0) ? (
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M11.05 1.8176C10.6199 0.949471 9.3801 0.949471 8.95003 1.8176L6.77815 6.20209L1.92047 6.90532C0.962761 7.04398 0.572374 8.22241 1.27187 8.90174L4.78527 12.3138L3.95598 17.1309C3.79046 18.0924 4.80071 18.8123 5.65465 18.365L10 16.0889L14.3455 18.365C15.1995 18.8125 16.2094 18.0921 16.044 17.1309L15.2147 12.3138L18.728 8.9018C19.4278 8.22236 19.037 7.04397 18.0795 6.90532L13.2219 6.20208L11.05 1.8176Z"
                                            fill="#FFBE33"
                                        />
                                    </svg>
                                ) : (
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M11.05 1.8176C10.6199 0.949471 9.3801 0.949471 8.95003 1.8176L6.77815 6.20209L1.92047 6.90532C0.962761 7.04398 0.572374 8.22241 1.27187 8.90174L4.78527 12.3138L3.95598 17.1309C3.79046 18.0924 4.80071 18.8123 5.65465 18.365L10 16.0889L14.3455 18.365C15.1995 18.8125 16.2094 18.0921 16.044 17.1309L15.2147 12.3138L18.728 8.9018C19.4278 8.22236 19.037 7.04397 18.0795 6.90532L13.2219 6.20208L11.05 1.8176ZM10 3.07725L8.04551 7.0229C7.87428 7.36856 7.54404 7.60683 7.16362 7.66192L2.79893 8.29379L5.95546 11.3592C6.23181 11.6277 6.35866 12.0154 6.29316 12.3961L5.54744 16.7278L9.45612 14.6805C9.79679 14.5022 10.2037 14.5025 10.5442 14.6806L14.4526 16.7278L13.7068 12.3959C13.6413 12.0152 13.7683 11.6276 14.0447 11.3591L17.2011 8.29378L12.8365 7.66192C12.4561 7.60687 12.1257 7.36854 11.9544 7.02268L10 3.07725Z"
                                            fill="#CBCBCD"
                                        />
                                    </svg>
                                )}
                            </button>
                        )
                    })}
                </div>
            </div>
            {description && (
                <div
                    className={clsx(`${baseClass}__description`, {
                        [`${className}__description`]: className,
                    })}
                >
                    {description}
                </div>
            )}
            {errorMessage && isShowErrorMessage && (
                <div
                    className={clsx(`${baseClass}__error`, {
                        [`${className}__error`]: className,
                    })}
                >
                    <div
                        className={clsx(`${baseClass}__error--icon`, {
                            [`${className}__error--icon`]: className,
                        })}
                    >
                        <WarningErrorIcon />
                    </div>
                    <div
                        className={clsx(`${baseClass}__error--text`, {
                            [`${className}__error--text`]: className,
                        })}
                    >
                        {errorMessage}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2InputRate
